import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ModalComponent from "../modal";
import InputComponent from "../input";
import CheckboxRadioComponent from "../checkbox-radio";
import SelectDropdownSecondaryComponent from "../select-dropdown-secondary";
import ButtonComponent from "../button";
import { otpOptions, providerInstitutionConstant, statusOptions } from "../../constants/user-constants";
import { useFormik } from "formik";
import { userSchema } from "../../helpers/schemas";
import TextareaComponent from "../textarea";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { ActionsContainer, FieldContainer, Form, FormError, Label } from "./add-update-user-modal.style";

const AddUpdateUserModal = ({ data, close, label, handleFormChange, handleFormSubmit, viewOnly, update, fromMakerChecker, slide }) => {
    const location = useLocation();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { institutions } = useSelector((state) => state.institutionsListReducer);
    const { rolesList } = useSelector((state) => state.usersReducer);

    // const notProvider = (user?.institution?.value !== providerInstitutionConstant?.value) && (!user?.superAdmin);
    const isProvider = (user?.institution?.value === providerInstitutionConstant?.value) && (user?.superAdmin);
    const isChecker = location.pathname.includes('checker');
    const isDisabledAll = isChecker || viewOnly;

    const [hasChanges, setHasChanges] = useState(false);

    const { handleChange, handleBlur, handleSubmit, validateForm, setFieldValue, touched, errors, dirty } = useFormik({
        initialValues: data,
        validationSchema: userSchema({isChecker, fromMakerChecker, is2FA: data?.is2FAEnable }),
        onSubmit: fromMakerChecker ? () => {} : () => {
            if (hasChanges) {
                handleFormSubmit();
            } else {
                toast.info('No changes found');
            }
        },
    });

    const handleAcceptReject = async (isAccept) => {
        const formErrors = await validateForm();

        if (Object.keys(formErrors).length < 1) {
            handleFormSubmit(isAccept);
        } else {
            handleSubmit();
        }
    };

    useEffect(() => {
        setHasChanges(dirty);
    }, [dirty]);

    const fields = [
        {
            label: 'Username',
            type: 'text',
            placeholder: 'Set Username',
            name: 'userId',
            value: data.userId,
            disabled: isDisabledAll || update,
        },
        {
            label: 'Name',
            type: 'text',
            placeholder: 'Enter Name',
            name: 'name',
            value: data.name,
            disabled: isDisabledAll,
        },
        {
            label: 'Institution',
            type: 'select',
            options: institutions,
            name: 'institution',
            value: data.institution,
            handleCustomChange: () => setFieldValue('role', ''),
            disabled: isDisabledAll || update || !isProvider,
        },
        {
            label: 'Role',
            type: 'select',
            required: true,
            options: rolesList,
            name: 'role',
            value: data.role,
            disabled: isDisabledAll || (data.institution === ''),
            hide: !data.institution || data.institution === '',
        },
        {
            label: 'National ID',
            type: 'text',
            placeholder: 'Enter CNIC Number',
            name: 'cnic',
            value: data.cnic,
            disabled: isDisabledAll || update,
        },
        {
            label: 'Mobile Number',
            type: 'text',
            placeholder: 'Enter Mobile Number',
            name: 'mobileNo',
            value: data.mobileNo,
            disabled: isDisabledAll,
        },
        {
            label: 'Email',
            type: 'email',
            placeholder: 'Enter Email',
            name: 'email',
            value: data.email,
            disabled: isDisabledAll,
        },
        {
            label: 'Employee Number',
            type: 'text',
            placeholder: 'Set Employee Number',
            name: 'empNumber',
            value: data.empNumber,
            disabled: isDisabledAll,
        },
        {
            label: 'Department',
            type: 'text',
            placeholder: 'Enter Department',
            name: 'department',
            value: data.department,
            disabled: isDisabledAll,
        },
        {
            label: 'Designation',
            type: 'text',
            placeholder: 'Enter Designation',
            name: 'designation',
            value: data.designation,
            disabled: isDisabledAll,
        },
        // {
        //     label: 'Remarks',
        //     type: 'text',
        //     placeholder: 'Enter Remarks',
        //     name: 'remarks',
        //     value: data.remarks,
        //     disabled: isDisabledAll,
        // },
        {
            label: 'Two-Factor Authentication (2FA)',
            type: 'checkbox',
            innerLabel: 'Enable 2FA',
            name: 'is2FAEnable',
            value: data.is2FAEnable,
            handleCustomChange: () => setFieldValue('otpType', ''),
            disabled: isDisabledAll,
        },
        {
            label: 'Send OTP Through',
            type: 'select',
            options: otpOptions,
            name: 'otpType',
            value: data.otpType,
            disabled: !data.is2FAEnable || isDisabledAll,
            hide: !data.is2FAEnable,
        },
        {
            label: 'Status',
            type: 'select',
            required: true,
            options: statusOptions,
            name: 'userStatus',
            value: data.userStatus,
            disabled: isDisabledAll,
        },
        // {
        //     label: 'Password',
        //     type: 'checkbox',
        //     innerLabel: 'Forget password',
        //     name: 'forgotPassword',
        //     value: data.forgotPassword,
        //     disabled: isDisabledAll,
        //     hide: !update || fromMakerChecker,
        // },
        ...(fromMakerChecker ? [{
            label: 'Comments',
            type: 'textarea',
            placeholder: 'Enter Comments',
            name: 'reason',
            value: data?.reason,
            fullCol: true,
        }]: []),
        {
            type: 'action',
            actions: fromMakerChecker ? [
                { label: 'Reject', type: 'button', color: 'error', outline: true, action: () => handleAcceptReject(false) },
                { label: isChecker ? 'Approve' : 'Resend', type: 'button', action: () => handleAcceptReject(true) }
            ] : [
                { label: viewOnly ? 'Close' : 'Cancel', type: 'button', outline: !viewOnly, action: close },
                ...(!viewOnly ? [{ label: update ? 'Update' : 'Submit', type: 'submit' }] : [])
            ],
            fullCol: true,
        }
    ];

    return (
        <ModalComponent close={close} label={label} width={700} slide={slide}>
            {/* MODAL BODY */}
            <Form onSubmit={handleSubmit}>
                {
                    fields && fields.length > 0 && fields.map((field, index) => (
                        <Fragment key={index}>
                            {
                                ['text', 'number', 'email', 'password'].includes(field.type) && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>{field.label}</Label> */}
                                    <InputComponent
                                        type={field.type}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        onBlur={handleBlur}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                ['checkbox', 'radio'].includes(field.type) && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <Label>{field.label}</Label>
                                    <CheckboxRadioComponent
                                        type={field.type}
                                        label={field.innerLabel}
                                        name={field.name}
                                        checked={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e); field.handleCustomChange && field.handleCustomChange() }}
                                        disabled={field.disabled}
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                field.type === 'select' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>{field.label}</Label> */}
                                    <SelectDropdownSecondaryComponent
                                        label={field.label}
                                        options={field.options}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e); field.handleCustomChange && field.handleCustomChange() }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {/* {
                                field.type === 'select' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <Label>{field.label}</Label>
                                    <SelectDropdownComponent
                                        options={field.options}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            } */}

                            {
                                field.type === 'textarea' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>Comments</Label> */}
                                    <TextareaComponent
                                        label='Comments'
                                        placeholder={field.placeholder}
                                        onBlur={handleBlur}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                field.type === 'action' && field.actions.length > 0 &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <ActionsContainer>
                                        {
                                            field.actions.map((button, index) => (
                                                <ButtonComponent key={index} label={button.label} type={button.type} outline={button.outline} color={button.color} onClick={button.action} />
                                            ))
                                        }
                                    </ActionsContainer>
                                </FieldContainer>
                            }
                        </Fragment>
                    ))
                }
            </Form>
        </ModalComponent>
    );
};

export default AddUpdateUserModal;